import { GraphsCard } from "@/components/cards/GraphsCard";
import { GaugeChart } from "@/components/graphs/GaugeChart";
import { PieGraph } from "@/components/graphs/PieGraph";
import { BarsGraph } from "@/components/graphs/BarsGraph";
import { PieChart, Legend } from "recharts";
import styles from "./css/EnergyContent.module.css";
import { Payload } from 'recharts/types/component/DefaultLegendContent';
import { SimpleYearComparisonChart } from "@/components/graphs/SimpleYearComparisonGraph";
import { DualPieChart } from "@/components/graphs/DualPieChart";
import { ChargingBarsGraph } from "@/components/graphs/ChargingBarsGraph";
import { AlternativeData } from "@/types";
import { formatNumberWithCommas } from "@/utils/numbers";
import { useTranslation } from "react-i18next";

interface EnergyContentProps {
    alternative: AlternativeData;
    consumptionColors: {
        energy_consumption: {
            [key: string]: string; 
        };
        energyProduction: {
            [key: string]: string;
        };
        vehicleCharging: {
            [key: string]: string;
        };
    };
    numberOfCompare?: number;
    energyRating?: string;
    layout?: 'default' | 'row';
}

export const EnergyContent: React.FC<EnergyContentProps> = ({
    consumptionColors,
    alternative,
    numberOfCompare = 0,
    energyRating,
    layout = 'default'
}) => {
    const { t } = useTranslation('EnergyContent');
    const consumptionColorsLocal = {
        energy_consumption: {
            "מגורים": "#007AFF",
            "מסחרי": "#FF2D55",
            "תעשייה": "#FFCC00",
            "מבני ציבור": "#7DE7FF",
        },
    }

    const energyProductionColors = {
        "גגות": "#34C759",
        "חזיתות": "#007AFF",
        "כמות יצור אנרגיה מצב קיים": "#FF2D55",
        "כמות יצור אנרגיה מצב עתידי": "#7DE7FF",
    };

    const chargingStationsColors = {
        "כמות עמדות טעינה במגורים": "#34C759",
        "כמות עמדות טעינה בשימושים אחרים": "#FF2D55",
    };

    const formatNumber = (number: number) => {
        if (typeof number !== 'number') return number;
        const roundedNum = Number(number.toFixed(2));
        const isWholeNumber = roundedNum % 1 === 0;
        const parts = roundedNum.toString().split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return isWholeNumber ? parts[0] : parts.join('.');
    };

    const contentClassName = `${styles.content} ${layout === 'row' ? styles.rowLayout : ''}`;

    return (
        <div className={contentClassName}>
            <GraphsCard 
                title={t('totalElectricityConsumption')}
                subtitle={t('energyRatingSubtitle', { rating: alternative.building_energy_rating })}
                children={
                    <DualPieChart
                        data={alternative.consumption_per_sqm}
                        colors={consumptionColorsLocal.energy_consumption}
                        size={numberOfCompare === 3 ? "xsmall" : "small"}
                        years={["מצב קיים", "סיום פרויקט"]}
                        labels={[t('currentStatus'), t('projectCompletion')]}
                    />
                }
                id="consumption"
                setFavourite={() => {}}
                isFavourite={false}
                showFavorite={false}
                numberOfCompare={numberOfCompare}
            />

             <GraphsCard 
                title={t('totalRenewableEnergyGeneration')}
                children={
                    <BarsGraph 
                        data={alternative.energy_production} 
                        colors={energyProductionColors} 
                        legendPosition="right"
                        isStacked={true}
                    />
                }
                id="energyProduction"
                setFavourite={() => {}}
                isFavourite={false}
                showFavorite={false}
                numberOfCompare={numberOfCompare}
                subtitle={t('chartUnitKw')}

            />
            <GraphsCard
                title={t('futureInstalledPower')}
                children={
                    <div style={{display: "flex", flexDirection: "column", justifyContent: "space-between"}}>
                        <div className={styles.headingContainer}>  
                            {alternative.future_installed_capacity && <span className={styles.heading}>Kwp</span>}
                            <h1 className={styles.heading}>
                                {formatNumberWithCommas(alternative.future_installed_capacity) || t('noValueEntered')}
                            </h1>
                        </div>  
                    </div>  
                }
                id="future_installed_capacity"
                setFavourite={() => {}}
                isFavourite={false}
                showFavorite={false}
                numberOfCompare={numberOfCompare}
            />
            <GraphsCard 
                title={t('chargingStationsTitle')} 
                children={
                    <ChargingBarsGraph 
                        data={alternative.charging_stations_forecast}
                        size={numberOfCompare === 3 ? "small" : "normal"} 
                    />
                } 
                id="vehicleCharging"
                setFavourite={() => {}}
                isFavourite={false}
                showFavorite={false}
                numberOfCompare={numberOfCompare}
            /> 
            <GraphsCard 
                title={t('energyResilienceTitle')} 
                children={
                    <GaugeChart 
                        data={alternative.energy_resilience["סיום פרויקט"].max} 
                        needle_value={alternative.energy_resilience["סיום פרויקט"].needle} 
                        year={t('projectCompletion')}
                        size={numberOfCompare === 3 ? "small": "default"}
                        showAsPercentage={true}
                    />
                }
                id="energyResilience"
                setFavourite={() => {}}
                isFavourite={false}
                showFavorite={false}
                numberOfCompare={numberOfCompare}
            />
        </div>
    );
}; 